import React from 'react';
import cx from 'classnames';
import {range as lRange, noop} from 'lodash';

import Link from './Link';
import {createURLWithParams} from '../util';

const PagerContainer = ({className, ...props}) => (
  <nav className={cx('relative z-0 flex flex-wrap justify-center items-center', className)} {...props} />
);

const PagerItem = ({
  href, ariaLabel, active, children, disabled, noBorder, onClick,
}) => (
  // eslint-disable-next-line jsx-a11y/anchor-is-valid
  <Link
    href={disabled ? '#' : href}
    onClick={!disabled ? onClick : undefined}
    aria-label={ariaLabel}
    className={cx(`
      relative 
      mr-2
      flex
      justify-center
      items-center
      h-10 
      w-10
      rounded-full 
      no-underline
      border-gray-400 
      bg-white 
      text-md 
      leading-5 
      font-medium 
      text-gray-700
      visited:text-gray-700
      hover:bg-gray-50
      transition ease-in-out 
      duration-150
      `, {
      'opacity-10 cursor-default': disabled,
      border: !noBorder,
      'border-purple-600': active,
    })}
  >
    {children}
  </Link>
);

const PagerDirectionButton = ({
  children, href, ariaLabel, onClick,
}) => (
  <Link
    href={href || ''}
    onClick={onClick}
    aria-label={ariaLabel}
    className={`
      relative 
      flex
      justify-center
      items-center
      h-10
      px-5
      rounded-full 
      bg-indigo
      no-underline
      text-md 
      leading-5 
      font-medium 
      text-white
      visited:text-white
      hover:text-white
      `}
  >
    {children}
  </Link>
);

const PagerEllipsis = () => (
  <span className="-ml-2 flex justify-center items-center h-16  w-8 text-lg text-gray-700">
    ...
  </span>
);

const Pager = ({
  pagination,
  maxPage = Infinity,
  showDirectionButtons = true,
  href,
  onClick = noop,
}) => {
  const {
    current,
    count,
    limit,
    hasMore,
  } = pagination;

  const pageCount = Math.min(maxPage, Math.ceil(count / limit));

  const calculateRange = () => {
    if (count === 0) return [];
    // generate range [current, current+1, current+2]
    if (current === 1 && hasMore) {
      const end = Math.min(pageCount - 1, 3);
      return lRange(1, end + 1);
      // generate range [current-2, current-1, current]
    } if (current === pageCount) {
      const dist = Math.min(pageCount - 1, 2);
      return lRange(current - dist, current + 1);
      // generate range [current-1, current, current+1]
    }
    const start = current > 1 ? current - 1 : current;
    const offSet = hasMore ? 2 : 1;
    return lRange(start, current + offSet);
  };
  const pageRange = calculateRange();

  const getPageURL = (pageDelta) => {
    const newPage = parseInt(current || 1, 10) + pageDelta;
    return href ? createURLWithParams(href, {
      page: newPage === 1 ? undefined : newPage, // Omit page=1
    }) : '#';
  };

  const handleClick = (pageDelta) => (e) => {
    if (!href) e.preventDefault();
    onClick(pageDelta);
  };

  return (
    <PagerContainer>
      {pageRange.length > 0 && (
        <>
          {current > 1 && showDirectionButtons && (
            <div className="mr-2">
              <PagerDirectionButton
                href={getPageURL(-1)}
                onClick={handleClick(-1)}
                ariaLabel="previous page"
              >
                Prev
              </PagerDirectionButton>
            </div>
          )}
          {pageRange[0] > 1 && (
            <PagerItem
              href={getPageURL(1 - current)}
              onClick={handleClick(1 - current)}
              ariaLabel="page 1"
              active={current === 1}
            >
              1
            </PagerItem>
          )}
          {pageRange[0] > 2 && (
            <PagerEllipsis />
          )}
          {pageRange.map((pageNumber) => (
            <PagerItem
              key={`question-list-page-${pageNumber}`}
              href={getPageURL(pageNumber - current)}
              onClick={handleClick(pageNumber - current)}
              ariaLabel={`page ${pageNumber}`}
              active={pageNumber === current}
            >
              {pageNumber}
            </PagerItem>
          ))}
          {pageRange[pageRange.length - 1] + 2 <= pageCount && (
            <PagerEllipsis />
          )}
          {
            pageRange[pageRange.length - 1] + 1 <= pageCount && (
              <PagerItem
                href={getPageURL(pageCount - current)}
                onClick={handleClick(pageCount - current)}
                ariaLabel={`page ${pageCount}`}
              >
                {pageCount}
              </PagerItem>
            )
          }
          {
            hasMore && current !== pageCount && showDirectionButtons && (
              <PagerDirectionButton
                href={getPageURL(1)}
                onClick={handleClick(1)}
                ariaLabel="next page"
              >
                Next
              </PagerDirectionButton>
            )
          }
        </>
      )}
    </PagerContainer>
  );
};

export default Pager;
